// Videos cortos (videos masivos de producto) — ADR-2026-10-22-videos-masivos-diseno-de-produccion.
//
// Todo lo que toca la red vive acá: la pantalla queda con estado y dibujo. El backend encola un video por producto
// (`/v1/videos/masivo`), con el id del lote como `execution_id` de todas las filas; acá se lanza, se consulta el estado
// de cada video y se cancela lo que todavía no arrancó.

import { atlasBackendFetch, atlasBackendGet } from '../../../../lib/atlasBackend';
import { firmarGenerada } from '../../../../lib/videoGenerado';
import { supabase } from '../../../../lib/supabase';

/** Precio fijo por video (espejo de VIDEO_MASIVO_CREDITOS del backend). */
export const CREDITOS_POR_VIDEO = 12;
/** Productos por tanda (espejo de VIDEO_MASIVO_MAX_PRODUCTOS). */
export const MAX_PRODUCTOS = 50;
export const MAX_INDICACION = 500;

export type VozDeVideo = 'mujer' | 'hombre';

export interface OpcionesDeLote {
  /** `null` = sin voz en off (y sin subtítulos). */
  voz: VozDeVideo | null;
  subtitulos: boolean;
  indicacion: string;
}

export interface EstadoDeVideo {
  job_id: string;
  variante_id: string | null;
  estado: 'en_cola' | 'generando' | 'listo' | 'fallo' | 'cancelado';
  etapa: string | null;
  error: string | null;
  creditos: number;
}

export interface EstadoDeLote {
  lote_id: string;
  total: number;
  listos: number;
  fallidos: number;
  cancelados: number;
  en_curso: number;
  terminado: boolean;
  videos: EstadoDeVideo[];
}

export interface VideoListo {
  id: string;
  varianteId: string | null;
  /** La ruta en el bucket `generadas`: la URL firmada vence a la hora y se vuelve a firmar con esto. */
  ruta: string;
  url: string;
  poster?: string;
  locucion: string | null;
}

/** Lo que cuesta una tanda. Puro. */
export const costoDeLote = (productos: number) => productos * CREDITOS_POR_VIDEO;

/** Qué está haciendo un video, en palabras de la persona. Puro. */
export function textoDeEtapa(v: Pick<EstadoDeVideo, 'estado' | 'etapa'>): string {
  if (v.estado === 'en_cola') return 'En la fila';
  if (v.estado === 'listo') return 'Listo';
  if (v.estado === 'fallo') return 'No salió';
  if (v.estado === 'cancelado') return 'Cancelado';
  switch (v.etapa) {
    case 'fotos':
      return 'Escribiendo el guion';
    case 'guion':
      return 'Grabando la voz';
    case 'voz':
      return 'Filmando el video';
    case 'motor':
      return 'Mejorando la calidad';
    case 'escalar':
      return 'Armando el video final';
    case 'armar':
    case 'upload_generadas':
    case 'deliver_and_charge':
      return 'Entregando';
    default:
      return 'Preparando';
  }
}

async function errorDe(res: Response): Promise<Error & { status?: number; tipo?: string }> {
  const j = (await res.json().catch(() => ({}))) as { error?: string; error_type?: string };
  const e = new Error(j.error && j.error !== 'no_disponible' ? j.error : 'No se pudo hacer el pedido. Probá de nuevo en un rato.') as Error & {
    status?: number;
    tipo?: string;
  };
  e.status = res.status;
  e.tipo = j.error_type;
  return e;
}

/** Lanza una tanda. El id del lote lo genera el front: un reintento con el mismo id no duplica nada. */
export async function lanzarLote(loteId: string, varianteIds: string[], o: OpcionesDeLote): Promise<{ creditosTotal: number }> {
  const res = await atlasBackendFetch('/v1/videos/masivo', {
    execution_id: loteId,
    variante_ids: varianteIds,
    voz: o.voz,
    subtitulos: o.voz ? o.subtitulos : false,
    ...(o.indicacion.trim() ? { indicacion: o.indicacion.trim().slice(0, MAX_INDICACION) } : {}),
  });
  if (res.status !== 202) throw await errorDe(res);
  const j = (await res.json()) as { creditos_total: number };
  return { creditosTotal: j.creditos_total };
}

export async function verLote(loteId: string): Promise<EstadoDeLote | null> {
  const res = await atlasBackendGet(`/v1/videos/masivo/${loteId}`);
  if (res.status === 404) return null;
  if (!res.ok) throw await errorDe(res);
  return (await res.json()) as EstadoDeLote;
}

export async function cancelarLote(loteId: string): Promise<number> {
  const res = await atlasBackendFetch(`/v1/videos/masivo/${loteId}/cancelar`, {});
  if (!res.ok) throw await errorDe(res);
  return ((await res.json()) as { cancelados: number }).cancelados;
}

/** Los videos ya entregados del lote (todas sus filas comparten el `execution_id` del lote), firmados. */
export async function videosListos(loteId: string, companyId: string): Promise<VideoListo[]> {
  const { data } = await supabase
    .from('imagenes_generadas')
    .select('id, file_name, variante_id, datos_generacion')
    .eq('id_company', companyId)
    .eq('execution_id', loteId)
    .eq('has_error', false);
  const filas = (data ?? []) as Array<{ id: string; file_name: string; variante_id: string | null; datos_generacion: Record<string, unknown> | null }>;
  const firmados = await Promise.all(
    filas.map(async (f): Promise<VideoListo | null> => {
      const ruta = `${companyId}/full/${f.file_name}`;
      const [url, poster] = await Promise.all([
        firmarGenerada(ruta),
        firmarGenerada(`${companyId}/opt/${f.file_name.replace(/\.mp4$/i, '.png')}`),
      ]);
      if (!url) return null;
      const extra = (f.datos_generacion?.video_masivo ?? null) as { locucion?: string | null } | null;
      return { id: f.id, varianteId: f.variante_id, ruta, url, ...(poster ? { poster } : {}), locucion: extra?.locucion ?? null };
    }),
  );
  return firmados.filter((v): v is VideoListo => !!v);
}

/** El lote en curso se recuerda por empresa: si la persona recarga, la pantalla lo retoma. */
const CLAVE = (companyId: string) => `atlas-videos-cortos-lote:${companyId}`;
export function loteRecordado(companyId: string): string | null {
  try {
    return localStorage.getItem(CLAVE(companyId));
  } catch {
    return null;
  }
}
export function recordarLote(companyId: string, loteId: string | null): void {
  try {
    if (loteId) localStorage.setItem(CLAVE(companyId), loteId);
    else localStorage.removeItem(CLAVE(companyId));
  } catch {
    // sin localStorage, sólo se pierde el retomar al recargar
  }
}
